import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise, BookOpen, CheckCircle, EnvelopeSimple, Gear, MagnifyingGlass, Package } from 'shared/icons';
import {
    ConversationThread,
    KindTag,
    KnowledgeManager,
    KnowledgePanel,
    MESSAGE_MAX_LENGTH,
    ProductDocumentModal,
    QUESTION_MAX_ANSWER_LENGTH,
    QuestionStatusPill,
    QuickRepliesManager,
    ReplyComposer,
    WaitingInfo,
} from '../components/atendimento/atendimentoShared';
import Button from '../components/ui/Button';
import ErrorCard from '../components/ui/ErrorCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatDateTimeBr } from '../utils/date';

/**
 * Atendimento — perguntas pré-venda + chat pós-venda do ML (redesign 2026-08-27,
 * padrão do Pós-venda). A visão padrão é UMA caixa de entrada priorizada por
 * tempo de espera do comprador (pergunta e chat juntos); Perguntas/Conversas
 * são as listas completas. O detalhe abre ao lado, em camadas: base de
 * conhecimento do produto em cima, thread + editor embaixo — a IA sugere a
 * resposta ancorada nas mesmas fichas que o operador vê.
 */

const PAGE_SIZE = 30;
// A caixa carrega as duas filas de ação inteiras (teto da rota = 200 cada).
const INBOX_LIMIT = 200;
const DAY_MS = 86_400_000;

const cardClass = 'rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950';

function fmtShortDate(iso) {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// ── Linhas das listas (2 andares, calmas — vermelho só na espera estourada) ──

const rowClass = (active) =>
    `flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
        active
            ? 'border-mono-400 bg-mono-50 dark:border-mono-600 dark:bg-mono-900/40'
            : 'border-mono-200 bg-white hover:bg-mono-50 dark:border-mono-800 dark:bg-mono-950 dark:hover:bg-mono-900/40'
    }`;

/** Miniatura do produto na linha — foto do anúncio quando existe, ícone senão. */
const RowThumb = ({ src, kind }) =>
    src ? (
        <img
            src={src}
            alt=""
            loading="lazy"
            className="h-11 w-11 shrink-0 rounded-lg border border-mono-200 object-cover dark:border-mono-800"
        />
    ) : (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-mono-100 text-mono-400 dark:bg-mono-900 dark:text-mono-600">
            {kind === 'question' ? <EnvelopeSimple size={20} /> : <Package size={20} />}
        </span>
    );

/** Chip do SKU — o elo com o documento do produto. */
const SkuChip = ({ sku }) =>
    sku ? (
        <span className="shrink-0 rounded bg-mono-100 px-1.5 font-mono text-[10px] font-semibold text-mono-600 dark:bg-mono-900 dark:text-mono-300">
            {sku}
        </span>
    ) : null;

/** Linha da caixa unificada: foto + manchete + espera; embaixo tipo, produto e SKU. */
const InboxRow = ({ row, active, onOpen }) => {
    const wait = WaitingInfo(row.waitedSince);
    const data = row.data;
    const headline =
        row.kind === 'question'
            ? data.question_text
            : `${data.buyer_nickname || 'Comprador'} aguarda resposta no chat`;
    const identity = [
        data.item_title || (row.kind === 'question' ? data.item_id : null),
        row.kind === 'conversation' && data.external_order_id ? `#${data.external_order_id}` : null,
        row.kind === 'question' ? data.buyer_nickname : null,
    ]
        .filter(Boolean)
        .join(' · ');
    return (
        <button type="button" onClick={() => onOpen(row)} className={rowClass(active)}>
            <RowThumb src={row.kind === 'question' ? data.item_thumbnail : null} kind={row.kind} />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex w-full items-baseline justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                        {wait?.breached && (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-danger-600 dark:bg-danger-500" />
                        )}
                        <span className="truncate text-sm font-medium text-black dark:text-white">{headline}</span>
                    </span>
                    {wait ? (
                        <span className={`shrink-0 text-[11px] font-semibold ${wait.tone}`}>{wait.label}</span>
                    ) : null}
                </span>
                <span className="flex w-full items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-1.5">
                        <KindTag kind={row.kind} />
                        <SkuChip sku={data.sku} />
                        <span className="truncate text-[11px] text-mono-500 dark:text-mono-400">{identity}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                        {row.kind === 'conversation' && Number(data.unread_count || 0) > 0 && (
                            <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">
                                {data.unread_count}
                            </span>
                        )}
                        {data.ai_suggestion ? (
                            <span title="Sugestão de IA pronta" className="text-xs">
                                💡
                            </span>
                        ) : null}
                    </span>
                </span>
            </span>
        </button>
    );
};

const QuestionRow = ({ question, active, onOpen }) => {
    const open = question.status === 'UNANSWERED';
    const wait = open ? WaitingInfo(question.question_date) : null;
    return (
        <button type="button" onClick={() => onOpen(question)} className={rowClass(active)}>
            <RowThumb src={question.item_thumbnail} kind="question" />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex w-full items-baseline justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                        {wait?.breached && (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-danger-600 dark:bg-danger-500" />
                        )}
                        <span className="truncate text-sm font-medium text-black dark:text-white">
                            {question.question_text}
                        </span>
                    </span>
                    {wait ? (
                        <span className={`shrink-0 text-[11px] font-semibold ${wait.tone}`}>{wait.label}</span>
                    ) : (
                        <span className="shrink-0 text-[11px] text-mono-400 dark:text-mono-600">
                            {fmtShortDate(question.question_date)}
                        </span>
                    )}
                </span>
                <span className="flex w-full items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-1.5">
                        <SkuChip sku={question.sku} />
                        <span className="truncate text-[11px] text-mono-500 dark:text-mono-400">
                            {[question.item_title || question.item_id, question.buyer_nickname]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                        {question.ai_suggestion && open ? (
                            <span title="Sugestão de IA pronta" className="text-xs">
                                💡
                            </span>
                        ) : null}
                        {!open ? <QuestionStatusPill status={question.status} /> : null}
                    </span>
                </span>
            </span>
        </button>
    );
};

const ConversationRow = ({ conversation, active, onOpen }) => {
    const wait = conversation.needs_reply ? WaitingInfo(conversation.last_message_at) : null;
    return (
        <button type="button" onClick={() => onOpen(conversation)} className={rowClass(active)}>
            <RowThumb src={null} kind="conversation" />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex w-full items-baseline justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                        {wait?.breached && (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-danger-600 dark:bg-danger-500" />
                        )}
                        <span className="truncate text-sm font-medium text-black dark:text-white">
                            {conversation.buyer_nickname ||
                                `Pedido ${conversation.external_order_id || conversation.pack_id}`}
                        </span>
                    </span>
                    {wait ? (
                        <span className={`shrink-0 text-[11px] font-semibold ${wait.tone}`}>{wait.label}</span>
                    ) : (
                        <span className="shrink-0 text-[11px] text-mono-400 dark:text-mono-600">
                            {fmtShortDate(conversation.last_message_at)}
                        </span>
                    )}
                </span>
                <span className="flex w-full items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-1.5">
                        <SkuChip sku={conversation.sku} />
                        <span className="truncate text-[11px] text-mono-500 dark:text-mono-400">
                            {[
                                conversation.item_title,
                                conversation.external_order_id ? `#${conversation.external_order_id}` : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                        {Number(conversation.unread_count || 0) > 0 && (
                            <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">
                                {conversation.unread_count}
                            </span>
                        )}
                        {conversation.needs_reply ? (
                            <span className="rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-bold text-warning-800 ring-1 ring-inset ring-warning-600/20 dark:bg-warning-400/10 dark:text-warning-300">
                                Responder
                            </span>
                        ) : null}
                    </span>
                </span>
            </span>
        </button>
    );
};

// ── Página ───────────────────────────────────────────────────────────────────

const Atendimento = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canManageQuickReplies = checkPermission('atendimento:quick_replies:manage');
    const canManageKnowledge = checkPermission('atendimento:knowledge:manage');

    const [view, setView] = useState('caixa'); // caixa | perguntas | conversas
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [q, setQ] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');

    // Caixa de entrada (duas filas de ação, mescladas client-side)
    const [inbox, setInbox] = useState(null); // { questions, conversations, truncated }
    const [inboxLoading, setInboxLoading] = useState(false);

    // Listas completas (servidor)
    const [questionsFilter, setQuestionsFilter] = useState('UNANSWERED');
    const [questionsPage, setQuestionsPage] = useState(0);
    const [questionsData, setQuestionsData] = useState(null); // { results, paging }
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [conversationsFilter, setConversationsFilter] = useState('needs_reply');
    const [conversationsPage, setConversationsPage] = useState(0);
    const [conversationsData, setConversationsData] = useState(null);
    const [conversationsLoading, setConversationsLoading] = useState(false);

    // Detalhe (compartilhado pelas três visões)
    const [selected, setSelected] = useState(null); // {kind:'question',question} | {kind:'conversation',conversation,messages}
    const [detailLoading, setDetailLoading] = useState(false);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [openReference, setOpenReference] = useState('');
    const [openingReference, setOpeningReference] = useState(false);

    // Base de conhecimento (contexto do produto selecionado + gestão)
    const [contextTarget, setContextTarget] = useState(null); // { sku, item_id }
    const [contextProduct, setContextProduct] = useState(null); // documento do SKU
    const [contextEntries, setContextEntries] = useState(null); // fichas + políticas
    const [contextLoading, setContextLoading] = useState(false);
    const [knowledgeOpen, setKnowledgeOpen] = useState(false);
    const [knowledgePrefill, setKnowledgePrefill] = useState(null);
    const [productModalOpen, setProductModalOpen] = useState(false);

    // Respostas rápidas
    const [quickReplies, setQuickReplies] = useState([]);
    const [quickRepliesOpen, setQuickRepliesOpen] = useState(false);

    const aiEnabled = Boolean(summary?.ai_enabled);
    const aiProvider = summary?.ai_provider || null;

    // Guarda contra resolução OBSOLETA: sugestão/envio/abertura são awaits de
    // segundos e o operador pode trocar de item no meio — o resultado só pode
    // aplicar detalhe/rascunho se o item de origem AINDA for o selecionado
    // (senão a resposta do chat A cairia no editor da pergunta B).
    const selectedRef = useRef(null);
    selectedRef.current = selected;
    const selectionSeqRef = useRef(0);
    // Mesma guarda para o fetch do contexto: resposta atrasada do item
    // anterior não pode sobrescrever o painel (nem alimentar o modal) do atual.
    const contextSeqRef = useRef(0);

    const stillSelected = (target) => {
        const current = selectedRef.current;
        if (!current || current.kind !== target.kind) {
            return false;
        }
        return target.kind === 'question'
            ? current.question.id === target.id
            : current.conversation.id === target.id;
    };

    // ── Loaders ─────────────────────────────────────────────────────────────

    const loadSummary = useCallback(async () => {
        try {
            setSummary(await api.getAtendimentoSummary());
        } catch {
            // resumo é cosmético; a fila continua funcionando sem ele
        }
    }, []);

    const loadQuickReplies = useCallback(async () => {
        try {
            const data = await api.getAtendimentoQuickReplies();
            setQuickReplies(Array.isArray(data?.results) ? data.results : []);
        } catch {
            // respostas rápidas são auxiliares; a fila funciona sem elas
        }
    }, []);

    const loadInbox = useCallback(async () => {
        try {
            setInboxLoading(true);
            setError('');
            const [questions, conversations] = await Promise.all([
                api.getAtendimentoQuestions({ status: 'UNANSWERED', limit: INBOX_LIMIT }),
                api.getAtendimentoConversations({ needs_reply: 'true', limit: INBOX_LIMIT }),
            ]);
            setInbox({
                questions: Array.isArray(questions?.results) ? questions.results : [],
                conversations: Array.isArray(conversations?.results) ? conversations.results : [],
                truncated:
                    Number(questions?.paging?.total || 0) > INBOX_LIMIT ||
                    Number(conversations?.paging?.total || 0) > INBOX_LIMIT,
            });
        } catch (loadError) {
            setError(loadError.message || 'Erro ao carregar a caixa de entrada');
        } finally {
            setInboxLoading(false);
        }
    }, []);

    const loadQuestions = useCallback(async () => {
        try {
            setQuestionsLoading(true);
            setError('');
            const params = { limit: PAGE_SIZE, offset: questionsPage * PAGE_SIZE };
            if (questionsFilter) {
                params.status = questionsFilter;
            }
            if (debouncedQ) {
                params.search = debouncedQ;
            }
            setQuestionsData(await api.getAtendimentoQuestions(params));
        } catch (loadError) {
            setError(loadError.message || 'Erro ao carregar perguntas');
        } finally {
            setQuestionsLoading(false);
        }
    }, [questionsPage, questionsFilter, debouncedQ]);

    const loadConversations = useCallback(async () => {
        try {
            setConversationsLoading(true);
            setError('');
            const params = { limit: PAGE_SIZE, offset: conversationsPage * PAGE_SIZE };
            if (conversationsFilter === 'needs_reply') {
                params.needs_reply = 'true';
            }
            if (debouncedQ) {
                params.search = debouncedQ;
            }
            setConversationsData(await api.getAtendimentoConversations(params));
        } catch (loadError) {
            setError(loadError.message || 'Erro ao carregar conversas');
        } finally {
            setConversationsLoading(false);
        }
    }, [conversationsPage, conversationsFilter, debouncedQ]);

    const loadContext = useCallback(async (target) => {
        const seq = ++contextSeqRef.current;
        // Limpa ANTES de buscar: o documento do SKU anterior não pode ficar no
        // painel (nem virar prefill do modal) enquanto o novo carrega.
        setContextProduct(null);
        setContextEntries(null);
        if (!target) {
            return;
        }
        try {
            setContextLoading(true);
            const params = {};
            if (target.item_id) {
                params.item_id = target.item_id;
            }
            if (target.sku) {
                params.sku = target.sku;
            }
            const data = await api.getAtendimentoContext(params);
            if (contextSeqRef.current !== seq) {
                return;
            }
            setContextProduct(data?.product || null);
            setContextEntries(Array.isArray(data?.knowledge) ? data.knowledge : []);
        } catch {
            if (contextSeqRef.current === seq) {
                setContextEntries([]);
            }
        } finally {
            if (contextSeqRef.current === seq) {
                setContextLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadSummary();
        loadQuickReplies();
    }, [loadSummary, loadQuickReplies]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQ(q.trim());
            setQuestionsPage(0);
            setConversationsPage(0);
        }, 350);
        return () => clearTimeout(timer);
    }, [q]);

    useEffect(() => {
        if (view === 'caixa') {
            loadInbox();
        }
    }, [view, loadInbox]);

    useEffect(() => {
        if (view === 'perguntas') {
            loadQuestions();
        }
    }, [view, loadQuestions]);

    useEffect(() => {
        if (view === 'conversas') {
            loadConversations();
        }
    }, [view, loadConversations]);

    useEffect(() => {
        loadContext(contextTarget);
    }, [contextTarget, loadContext]);

    // ── Classificação da caixa (client-side, memoizada) ─────────────────────

    const inboxView = useMemo(() => {
        if (!inbox) {
            return null;
        }
        const term = debouncedQ.toLowerCase();
        const matches = (values) =>
            !term || values.filter(Boolean).some((value) => String(value).toLowerCase().includes(term));
        const rows = [];
        for (const question of inbox.questions) {
            if (question.status !== 'UNANSWERED') {
                continue;
            }
            if (!matches([question.question_text, question.item_title, question.item_id, question.sku, question.buyer_nickname])) {
                continue;
            }
            rows.push({ kind: 'question', id: question.id, waitedSince: question.question_date, data: question });
        }
        for (const conversation of inbox.conversations) {
            if (!conversation.needs_reply) {
                continue;
            }
            if (
                !matches([
                    conversation.buyer_nickname,
                    conversation.item_title,
                    conversation.sku,
                    conversation.external_order_id,
                    conversation.pack_id,
                ])
            ) {
                continue;
            }
            rows.push({
                kind: 'conversation',
                id: conversation.id,
                waitedSince: conversation.last_message_at,
                data: conversation,
            });
        }
        // Quem espera há mais tempo primeiro; sem data vai para o fim.
        const waitedMs = (row) => {
            const ts = row.waitedSince ? new Date(row.waitedSince).getTime() : Number.NaN;
            return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
        };
        rows.sort((a, b) => waitedMs(a) - waitedMs(b));
        const now = Date.now();
        const bands = [
            { key: 'estourado', label: 'Esperando há mais de 24 h', items: [] },
            { key: 'hoje', label: 'Últimas 24 h', items: [] },
        ];
        for (const row of rows) {
            const ts = waitedMs(row);
            if (Number.isFinite(ts) && now - ts >= DAY_MS) {
                bands[0].items.push(row);
            } else {
                bands[1].items.push(row);
            }
        }
        return { rows, bands, truncated: inbox.truncated };
    }, [inbox, debouncedQ]);

    // ── Seleção ─────────────────────────────────────────────────────────────

    const selectQuestion = (question) => {
        selectionSeqRef.current += 1;
        setSelected({ kind: 'question', question });
        setDraft(question.ai_suggestion || '');
        setContextTarget({ sku: question.sku || null, item_id: question.item_id || null });
    };

    const selectConversation = async (conversation) => {
        const seq = ++selectionSeqRef.current;
        try {
            setDetailLoading(true);
            const detail = await api.getAtendimentoConversation(conversation.id);
            // Abrir marca como lida no ML → zera o badge local também (mesmo
            // que o operador já tenha pulado para outro item).
            const zeroUnread = (rows) =>
                rows.map((row) => (row.id === conversation.id ? { ...row, unread_count: 0 } : row));
            setInbox((current) =>
                current ? { ...current, conversations: zeroUnread(current.conversations) } : current,
            );
            setConversationsData((current) =>
                current ? { ...current, results: zeroUnread(current.results || []) } : current,
            );
            if (selectionSeqRef.current !== seq) {
                return;
            }
            setSelected({ kind: 'conversation', ...detail });
            setDraft(detail?.conversation?.ai_suggestion || '');
            // As chaves do produto vêm do detalhe (product_keys resolve o
            // item da venda) — o painel enxerga as MESMAS fichas que a IA usa.
            setContextTarget({
                sku: detail?.product_keys?.sku ?? detail?.conversation?.sku ?? null,
                item_id: detail?.product_keys?.item_id ?? null,
            });
        } catch (detailError) {
            toast.error(detailError.message || 'Falha ao abrir a conversa');
        } finally {
            setDetailLoading(false);
        }
    };

    const openInboxRow = (row) => {
        if (row.kind === 'question') {
            selectQuestion(row.data);
        } else {
            selectConversation(row.data);
        }
    };

    // ── Ações ───────────────────────────────────────────────────────────────

    const replaceQuestionRow = (updated) => {
        setInbox((current) =>
            current
                ? {
                      ...current,
                      questions: current.questions.map((row) => (row.id === updated.id ? updated : row)),
                  }
                : current,
        );
        setQuestionsData((current) =>
            current
                ? {
                      ...current,
                      results: (current.results || []).map((row) => (row.id === updated.id ? updated : row)),
                  }
                : current,
        );
    };

    const replaceConversationRow = (updated) => {
        setInbox((current) =>
            current
                ? {
                      ...current,
                      conversations: current.conversations.map((row) => (row.id === updated.id ? updated : row)),
                  }
                : current,
        );
        setConversationsData((current) =>
            current
                ? {
                      ...current,
                      results: (current.results || []).map((row) => (row.id === updated.id ? updated : row)),
                  }
                : current,
        );
    };

    const selectionTarget = () =>
        selected.kind === 'question'
            ? { kind: 'question', id: selected.question.id }
            : { kind: 'conversation', id: selected.conversation.id };

    const handleSuggest = async () => {
        if (!selected) {
            return;
        }
        const target = selectionTarget();
        try {
            setSuggesting(true);
            if (target.kind === 'question') {
                const updated = await api.suggestAtendimentoQuestion(target.id);
                replaceQuestionRow(updated);
                if (stillSelected(target)) {
                    setSelected({ kind: 'question', question: updated });
                    setDraft(updated.ai_suggestion || '');
                }
            } else {
                const updated = await api.suggestAtendimentoConversation(target.id);
                replaceConversationRow(updated);
                if (stillSelected(target)) {
                    setSelected((current) => (current ? { ...current, conversation: updated } : current));
                    setDraft(updated.ai_suggestion || '');
                }
            }
        } catch (suggestError) {
            toast.error(suggestError.message || 'Falha ao gerar sugestão');
        } finally {
            setSuggesting(false);
        }
    };

    const handleSend = async () => {
        if (!selected || !draft.trim()) {
            return;
        }
        const target = selectionTarget();
        const text = draft.trim();
        try {
            setSending(true);
            if (target.kind === 'question') {
                const updated = await api.answerAtendimentoQuestion(target.id, text);
                replaceQuestionRow(updated);
                if (stillSelected(target)) {
                    setSelected({ kind: 'question', question: updated });
                    setDraft('');
                }
                toast.success('Resposta publicada no Mercado Livre.');
            } else {
                const detail = await api.sendAtendimentoMessage(target.id, text);
                if (detail?.conversation) {
                    replaceConversationRow(detail.conversation);
                }
                if (stillSelected(target)) {
                    setSelected({ kind: 'conversation', ...detail });
                    setDraft('');
                }
                toast.success('Mensagem enviada.');
            }
            loadSummary();
        } catch (sendError) {
            toast.error(sendError.message || 'Falha ao enviar');
        } finally {
            setSending(false);
        }
    };

    const handleOpenReference = async () => {
        if (!openReference.trim()) {
            return;
        }
        try {
            setOpeningReference(true);
            const conversation = await api.openAtendimentoConversation(openReference.trim());
            setOpenReference('');
            loadConversations();
            await selectConversation(conversation);
        } catch (openError) {
            toast.error(openError.message || 'Nenhuma conversa encontrada para esse pedido/pack');
        } finally {
            setOpeningReference(false);
        }
    };

    const refresh = useCallback(() => {
        loadSummary();
        if (view === 'caixa') {
            loadInbox();
        } else if (view === 'perguntas') {
            loadQuestions();
        } else {
            loadConversations();
        }
    }, [view, loadSummary, loadInbox, loadQuestions, loadConversations]);

    const handleSync = async () => {
        try {
            setSyncing(true);
            await api.syncAtendimento();
            refresh();
            toast.success('Sincronizado com o Mercado Livre.');
        } catch (syncError) {
            toast.error(syncError.message || 'Falha ao sincronizar com o Mercado Livre');
        } finally {
            setSyncing(false);
        }
    };

    // Cola a resposta rápida SEM sobrescrever o que o operador já digitou.
    const pasteQuickReply = (body) => {
        setDraft((current) => (current && current.trim() ? `${current}\n${body}` : body));
    };

    const openKnowledgeManager = (prefill = null) => {
        setKnowledgePrefill(prefill);
        setKnowledgeOpen(true);
    };

    // ── Render ──────────────────────────────────────────────────────────────

    const renderPagination = (data, page, setPage, loading) => {
        const total = Number(data?.paging?.total || 0);
        if (total <= PAGE_SIZE) {
            return null;
        }
        return (
            <div className="flex items-center justify-between text-sm text-mono-500">
                <span>
                    {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
                </span>
                <div className="flex gap-2">
                    <Button size="sm" variant="ghost" disabled={loading || page === 0} onClick={() => setPage(page - 1)}>
                        Anterior
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        disabled={loading || (page + 1) * PAGE_SIZE >= total}
                        onClick={() => setPage(page + 1)}
                    >
                        Próxima
                    </Button>
                </div>
            </div>
        );
    };

    const filterChip = (active, onClick, children) => (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                active
                    ? 'bg-black text-white dark:bg-white dark:text-black'
                    : 'bg-mono-100 text-mono-600 hover:bg-mono-200 dark:bg-mono-900 dark:text-mono-300 dark:hover:bg-mono-800'
            }`}
        >
            {children}
        </button>
    );

    const emptyState = (title, hint) => (
        <div className="rounded-2xl border border-dashed border-mono-300 py-14 text-center dark:border-mono-700">
            <p className="text-sm font-medium text-mono-600 dark:text-mono-300">{title}</p>
            <p className="mt-1 text-xs text-mono-400">{hint}</p>
        </div>
    );

    const renderQuestionDetail = (question) => (
        <div className={cardClass}>
            <div className="flex items-start justify-between gap-3 border-b border-mono-200 p-4 dark:border-mono-800">
                <div className="flex min-w-0 items-center gap-3">
                    {question.item_thumbnail ? (
                        <img
                            src={question.item_thumbnail}
                            alt=""
                            className="h-10 w-10 shrink-0 rounded-lg border border-mono-200 object-cover dark:border-mono-800"
                        />
                    ) : null}
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-black dark:text-white">
                            {question.item_title || question.item_id}
                        </p>
                        <p className="truncate font-mono text-[11px] text-mono-500 dark:text-mono-400">
                            {[question.item_id, question.sku ? `SKU ${question.sku}` : null, question.buyer_nickname]
                                .filter(Boolean)
                                .join(' · ')}
                        </p>
                    </div>
                </div>
                <QuestionStatusPill status={question.status} />
            </div>
            <div className="space-y-4 p-4">
                <div className="rounded-2xl rounded-bl-md bg-mono-100 p-3 text-sm text-black dark:bg-mono-900 dark:text-white">
                    <p className="whitespace-pre-wrap">{question.question_text}</p>
                    <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                        {FormatDateTimeBr(question.question_date, { blank: '—' })}
                    </p>
                </div>

                {question.status === 'ANSWERED' && question.answer_text ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-950">
                        <p className="mb-1 flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                            <CheckCircle size={14} /> Resposta publicada
                        </p>
                        <p className="whitespace-pre-wrap text-black dark:text-white">{question.answer_text}</p>
                        <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                            {FormatDateTimeBr(question.answer_date, { blank: '—' })}
                        </p>
                    </div>
                ) : question.status === 'UNANSWERED' ? (
                    <ReplyComposer
                        value={draft}
                        onChange={setDraft}
                        maxLength={QUESTION_MAX_ANSWER_LENGTH}
                        rows={5}
                        placeholder="Escreva a resposta ou gere uma sugestão com IA..."
                        quickReplies={quickReplies}
                        onPickQuickReply={pasteQuickReply}
                        aiEnabled={aiEnabled}
                        aiProvider={aiProvider}
                        suggesting={suggesting}
                        onSuggest={handleSuggest}
                        suggestionAt={question.ai_suggestion_at}
                        sending={sending}
                        onSend={handleSend}
                        sendLabel="Publicar resposta"
                    />
                ) : (
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        Esta pergunta não está aberta para resposta.
                    </p>
                )}
            </div>
        </div>
    );

    const renderConversationDetail = (conversation, messages) => (
        <div className={`${cardClass} flex flex-col`}>
            <div className="border-b border-mono-200 p-4 dark:border-mono-800">
                <p className="text-sm font-semibold text-black dark:text-white">
                    {conversation.buyer_nickname || `Pedido ${conversation.external_order_id || conversation.pack_id}`}
                </p>
                <p className="truncate font-mono text-[11px] text-mono-500 dark:text-mono-400">
                    {[
                        conversation.item_title,
                        conversation.sku ? `SKU ${conversation.sku}` : null,
                        conversation.external_order_id ? `Pedido #${conversation.external_order_id}` : null,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </p>
            </div>
            <div className="p-4">
                <ConversationThread conversation={conversation} messages={messages} />
            </div>
            {conversation.conversation_status === 'BLOCKED' ? (
                <p className="border-t border-mono-200 p-4 text-sm text-mono-500 dark:border-mono-800 dark:text-mono-400">
                    Conversa bloqueada pelo Mercado Livre — não é possível enviar mensagens.
                </p>
            ) : (
                <div className="border-t border-mono-200 p-4 dark:border-mono-800">
                    <ReplyComposer
                        value={draft}
                        onChange={setDraft}
                        maxLength={MESSAGE_MAX_LENGTH}
                        rows={3}
                        placeholder="Escreva a mensagem ou gere uma sugestão com IA..."
                        quickReplies={quickReplies}
                        onPickQuickReply={pasteQuickReply}
                        aiEnabled={aiEnabled}
                        aiProvider={aiProvider}
                        suggesting={suggesting}
                        onSuggest={handleSuggest}
                        suggestionAt={conversation.ai_suggestion_at}
                        sending={sending}
                        onSend={handleSend}
                    />
                </div>
            )}
        </div>
    );

    const renderDetailColumn = () => {
        if (detailLoading && !selected) {
            return (
                <div className={`${cardClass} flex min-h-[300px] items-center justify-center`}>
                    <LoadingSpinner />
                </div>
            );
        }
        if (!selected) {
            return (
                <div
                    className={`flex min-h-[300px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-mono-300 p-8 text-mono-400 dark:border-mono-700`}
                >
                    <EnvelopeSimple size={32} />
                    <p className="text-sm">Selecione um item da fila para atender</p>
                </div>
            );
        }
        return (
            <div className="flex flex-col gap-3">
                <KnowledgePanel
                    product={contextProduct}
                    entries={contextEntries}
                    loading={contextLoading}
                    canManage={canManageKnowledge}
                    targetSku={contextTarget?.sku || null}
                    onEditProduct={() => setProductModalOpen(true)}
                    onCreateFicha={() =>
                        openKnowledgeManager({
                            sku: contextTarget?.sku || '',
                            item_id: contextTarget?.item_id || '',
                        })
                    }
                    onEditFicha={(entry) => openKnowledgeManager({ entry })}
                />
                {selected.kind === 'question'
                    ? renderQuestionDetail(selected.question)
                    : renderConversationDetail(selected.conversation, selected.messages)}
            </div>
        );
    };

    const inboxCount =
        summary === null
            ? null
            : Number(summary.questions_unanswered || 0) + Number(summary.conversations_needing_reply || 0);
    const segments = [
        { key: 'caixa', label: 'Caixa de entrada', count: inboxCount },
        { key: 'perguntas', label: 'Perguntas', count: summary?.questions_unanswered ?? null },
        { key: 'conversas', label: 'Conversas', count: summary?.conversations_needing_reply ?? null },
    ];

    const activeLoading =
        view === 'caixa' ? inboxLoading && !inboxView : view === 'perguntas' ? questionsLoading && !questionsData : conversationsLoading && !conversationsData;

    return (
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
            {/* ── Topo: busca e ações ── */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative mr-auto">
                    <MagnifyingGlass
                        size={15}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mono-400"
                    />
                    <input
                        value={q}
                        onChange={(event) => setQ(event.target.value)}
                        placeholder="Buscar pergunta, produto, SKU, pedido, comprador…"
                        className="w-80 rounded-lg border border-mono-300 bg-white py-1.5 pl-9 pr-3 text-sm outline-none transition-colors focus:border-black dark:border-mono-700 dark:bg-mono-950 dark:focus:border-white"
                    />
                </div>
                {canManageKnowledge ? (
                    <Button size="sm" variant="secondary" onClick={() => openKnowledgeManager(null)}>
                        <BookOpen size={16} className="mr-1" /> Conhecimento
                    </Button>
                ) : null}
                {canManageQuickReplies ? (
                    <Button size="sm" variant="secondary" onClick={() => setQuickRepliesOpen(true)}>
                        <Gear size={16} className="mr-1" /> Respostas rápidas
                    </Button>
                ) : null}
                <Button size="sm" variant="secondary" onClick={handleSync} disabled={syncing}>
                    <ArrowClockwise size={16} className={`mr-1 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Sincronizando...' : 'Sincronizar'}
                </Button>
                <Button size="sm" variant="ghost" onClick={refresh} title="Recarregar">
                    <ArrowClockwise size={16} />
                </Button>
            </div>

            {/* ── Visões ── */}
            <div className="flex flex-wrap items-center gap-2">
                {segments.map((segment) => (
                    <button
                        key={segment.key}
                        type="button"
                        onClick={() => setView(segment.key)}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                            view === segment.key
                                ? 'bg-black text-white dark:bg-white dark:text-black'
                                : 'bg-white text-mono-700 ring-1 ring-inset ring-mono-200 hover:bg-mono-100 dark:bg-mono-950 dark:text-mono-300 dark:ring-mono-800 dark:hover:bg-mono-900'
                        }`}
                    >
                        {segment.label}
                        {segment.count !== null && (
                            <span
                                className={`rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
                                    view === segment.key
                                        ? 'bg-white/20 dark:bg-black/15'
                                        : 'bg-mono-100 text-mono-500 dark:bg-mono-900 dark:text-mono-400'
                                }`}
                            >
                                {segment.count}
                            </span>
                        )}
                    </button>
                ))}
                {!aiEnabled && summary && (
                    <span className="ml-auto text-xs text-mono-500 dark:text-mono-400">
                        IA desativada — defina GEMINI_API_KEY (ou ANTHROPIC_API_KEY) no backend
                    </span>
                )}
            </div>

            {error && (
                <ErrorCard
                    title="Falha ao carregar o atendimento"
                    message={error}
                    action="Tentar novamente"
                    actionFn={refresh}
                    type="error"
                />
            )}

            {activeLoading ? (
                <div className="flex justify-center py-16">
                    <LoadingSpinner />
                </div>
            ) : (
                <div className="grid items-start gap-4 lg:grid-cols-[minmax(340px,5fr)_7fr]">
                    {/* ── Coluna esquerda: a fila ── */}
                    <div className="flex max-h-[75vh] flex-col gap-2 overflow-y-auto pr-1">
                        {view === 'caixa' && (
                            <>
                                {inboxView?.rows.length === 0 &&
                                    emptyState(
                                        'Caixa zerada 🎉',
                                        'Nenhum comprador esperando resposta agora. Use "Sincronizar" para conferir no ML.',
                                    )}
                                {inboxView?.bands
                                    .filter((band) => band.items.length > 0)
                                    .map((band) => (
                                        <div key={band.key} className="flex flex-col gap-2">
                                            <p className="mt-2 px-1 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                                                {band.label} ({band.items.length})
                                            </p>
                                            {band.items.map((row) => (
                                                <InboxRow
                                                    key={`${row.kind}:${row.id}`}
                                                    row={row}
                                                    active={
                                                        selected
                                                            ? (selected.kind === 'question' &&
                                                                  row.kind === 'question' &&
                                                                  selected.question.id === row.id) ||
                                                              (selected.kind === 'conversation' &&
                                                                  row.kind === 'conversation' &&
                                                                  selected.conversation.id === row.id)
                                                            : false
                                                    }
                                                    onOpen={openInboxRow}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                {inboxView?.truncated && (
                                    <p className="px-1 text-[11px] text-mono-400">
                                        Mostrando os {INBOX_LIMIT} mais recentes de cada fila — responda para liberar o
                                        resto.
                                    </p>
                                )}
                            </>
                        )}

                        {view === 'perguntas' && (
                            <>
                                <div className="flex gap-2 px-1">
                                    {filterChip(
                                        questionsFilter === 'UNANSWERED',
                                        () => {
                                            setQuestionsFilter('UNANSWERED');
                                            setQuestionsPage(0);
                                        },
                                        'Aguardando resposta',
                                    )}
                                    {filterChip(
                                        questionsFilter === '',
                                        () => {
                                            setQuestionsFilter('');
                                            setQuestionsPage(0);
                                        },
                                        'Todas',
                                    )}
                                </div>
                                {(questionsData?.results || []).length === 0 &&
                                    emptyState(
                                        'Nenhuma pergunta aqui',
                                        'Use "Sincronizar" para buscar no Mercado Livre.',
                                    )}
                                {(questionsData?.results || []).map((question) => (
                                    <QuestionRow
                                        key={question.id}
                                        question={question}
                                        active={selected?.kind === 'question' && selected.question.id === question.id}
                                        onOpen={selectQuestion}
                                    />
                                ))}
                                {renderPagination(questionsData, questionsPage, setQuestionsPage, questionsLoading)}
                            </>
                        )}

                        {view === 'conversas' && (
                            <>
                                <div className="flex gap-2 px-1">
                                    {filterChip(
                                        conversationsFilter === 'needs_reply',
                                        () => {
                                            setConversationsFilter('needs_reply');
                                            setConversationsPage(0);
                                        },
                                        'Aguardando resposta',
                                    )}
                                    {filterChip(
                                        conversationsFilter === '',
                                        () => {
                                            setConversationsFilter('');
                                            setConversationsPage(0);
                                        },
                                        'Todas',
                                    )}
                                </div>
                                <div className="flex gap-2 px-1">
                                    <input
                                        type="text"
                                        value={openReference}
                                        onChange={(event) => setOpenReference(event.target.value)}
                                        placeholder="Abrir conversa pelo nº do pedido/pack"
                                        className="w-full rounded-lg border border-mono-200 bg-white px-3 py-2 text-sm text-black outline-none focus:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-white"
                                    />
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleOpenReference}
                                        disabled={openingReference || !openReference.trim()}
                                    >
                                        {openingReference ? '...' : 'Abrir'}
                                    </Button>
                                </div>
                                {(conversationsData?.results || []).length === 0 &&
                                    emptyState(
                                        'Nenhuma conversa aqui',
                                        'Conversas aparecem quando um comprador manda mensagem — ou abra uma pelo número do pedido.',
                                    )}
                                {(conversationsData?.results || []).map((conversation) => (
                                    <ConversationRow
                                        key={conversation.id}
                                        conversation={conversation}
                                        active={
                                            selected?.kind === 'conversation' &&
                                            selected.conversation.id === conversation.id
                                        }
                                        onOpen={selectConversation}
                                    />
                                ))}
                                {renderPagination(
                                    conversationsData,
                                    conversationsPage,
                                    setConversationsPage,
                                    conversationsLoading,
                                )}
                            </>
                        )}
                    </div>

                    {/* ── Coluna direita: detalhe em camadas ── */}
                    {renderDetailColumn()}
                </div>
            )}

            {canManageQuickReplies ? (
                <QuickRepliesManager
                    isOpen={quickRepliesOpen}
                    onClose={() => setQuickRepliesOpen(false)}
                    quickReplies={quickReplies}
                    onChanged={loadQuickReplies}
                />
            ) : null}
            {canManageKnowledge ? (
                <KnowledgeManager
                    isOpen={knowledgeOpen}
                    onClose={() => setKnowledgeOpen(false)}
                    onChanged={() => loadContext(contextTarget)}
                    prefill={knowledgePrefill}
                />
            ) : null}
            {canManageKnowledge ? (
                <ProductDocumentModal
                    isOpen={productModalOpen}
                    onClose={() => setProductModalOpen(false)}
                    sku={contextTarget?.sku || ''}
                    product={contextProduct}
                    onSaved={() => loadContext(contextTarget)}
                />
            ) : null}
        </div>
    );
};

export default Atendimento;
